import type {
  AICourseCreatorGenerationPhase,
  AICourseCreatorGenerationStatus,
  AICourseCreatorMessageRole,
  IAICourseCreatorGeneratedCoursePreview,
  IAICourseCreatorGeneratedPagePreview,
  IAICourseCreatorSessionState,
} from './aiCourseCreatorAgent';
import type { TCourseViewModel, TTreeViewModel } from '../models/CourseViewModels';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export interface IAICourseCreatorSignalRProgressUpdate {
  courseId: number | null;
  currentState: string | null;
  message: string | null;
}

export const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';

export const normalizeNumericId = (value: number | string, fieldName: string): number => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) {
    throw new Error(`Invalid ${fieldName} returned from AI service.`);
  }
  return normalized;
};

export const readStringValue = (source: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

export const readNumericIdValue = (source: Record<string, unknown>, keys: string[], fieldName: string): number | null => {
  for (const key of keys) {
    const value = source[key];
    if (value === null || value === undefined || value === '') continue;
    if (typeof value !== 'number' && typeof value !== 'string') {
      throw new Error(`Invalid ${fieldName} returned from AI service.`);
    }
    return normalizeNumericId(value as number | string, fieldName);
  }
  return null;
};

export const unwrapSignalRProgressEnvelope = (envelope: unknown): Record<string, unknown> | null => {
  if (!isRecord(envelope)) return null;

  if ('success' in envelope) {
    if (envelope.success !== true) {
      const errorMessage = typeof envelope.errorMessage === 'string' && envelope.errorMessage ? envelope.errorMessage : GENERIC_ERROR_MESSAGE;
      throw new Error(errorMessage);
    }

    return isRecord(envelope.data) ? envelope.data : null;
  }

  return envelope;
};

export const readSignalRProgressUpdate = (envelope: unknown): IAICourseCreatorSignalRProgressUpdate | null => {
  const payload = unwrapSignalRProgressEnvelope(envelope);
  if (!payload) return null;

  return {
    courseId: readNumericIdValue(payload, ['courseId', 'courseID', 'CourseId', 'CourseID'], 'courseId'),
    currentState: readStringValue(payload, ['currentState', 'CurrentState']),
    message: readStringValue(payload, ['messageToClient', 'MessageToClient', 'statusMessage', 'StatusMessage', 'message', 'Message']),
  };
};

export const normalizeSignalRRole = (role: unknown): AICourseCreatorMessageRole => (role === 'user' ? 'user' : 'assistant');

export const normalizeSignalRMessage = (message: unknown): string => {
  if (typeof message === 'string') return message;
  if (message === null || message === undefined) return '';
  return String(message);
};

export const mapSessionState = (stateKey: string): IAICourseCreatorSessionState => {
  const normalized = stateKey.trim().toLowerCase();

  return {
    isInterrupted: normalized.includes('interrupt'),
    isComplete: normalized === 'completed' || normalized === 'course completed' || normalized.includes('fully complete'),
  };
};

export const mapSignalRStateToGenerationPhase = (stateKey: string): AICourseCreatorGenerationPhase | null => {
  const normalized = stateKey.trim().toLowerCase();

  if (mapSessionState(stateKey).isComplete) return 'complete';
  if (normalized.includes('physical assembly') || normalized.includes('ingestingpagecontent') || (normalized.includes('ingesting') && normalized.includes('page content'))) return 'assembly';
  if (normalized.includes('generation complete')) return 'assembly';
  if (normalized.includes('generatingpagecontent') || normalized.includes('generating content') || (normalized.includes('generating') && normalized.includes('page content'))) return 'content';
  if (normalized === 'ingesting' || normalized.includes('ingestingcourseoutline') || (normalized.includes('ingesting') && normalized.includes('outline'))) return 'structure';
  if (normalized.includes('generatingcourseoutline') || normalized.includes('generating outline') || (normalized.includes('generating') && normalized.includes('outline'))) return 'outline';

  return null;
};

export const mapSignalRStateToGenerationStatus = (stateKey: string): AICourseCreatorGenerationStatus => {
  const normalized = stateKey.toLowerCase();
  if (normalized.includes('fail') || normalized.includes('error')) return 'failed';
  if (mapSessionState(stateKey).isComplete) return 'complete';
  if (normalized.includes('idle')) return 'idle';
  if (
    normalized.includes('interview') ||
    normalized.includes('context') ||
    normalized.includes('collect') ||
    normalized.includes('question') ||
    normalized.includes('prompt') ||
    normalized.includes('input')
  ) {
    return 'collecting-context';
  }
  return 'generating-course';
};

export const resolveStickyGenerationStatus = (status: AICourseCreatorGenerationStatus, sessionLooksComplete: boolean): AICourseCreatorGenerationStatus =>
  sessionLooksComplete && status !== 'failed' ? 'complete' : status;

export const resolveGeneratedCourseStatus = (sessionLooksComplete: boolean, latestGenerationStatus: AICourseCreatorGenerationStatus, pageCount: number): AICourseCreatorGenerationStatus =>
  sessionLooksComplete || latestGenerationStatus === 'complete'
    ? 'complete'
    : latestGenerationStatus === 'failed'
      ? 'failed'
      : pageCount === 0 && latestGenerationStatus === 'idle'
        ? 'collecting-context'
        : latestGenerationStatus === 'idle'
          ? 'generating-course'
          : latestGenerationStatus;

export const flattenPageTreeWithDepth = (pages: TTreeViewModel[], depth = 0): { node: TTreeViewModel; depth: number }[] =>
  pages.flatMap((page) => [{ node: page, depth }, ...flattenPageTreeWithDepth(page.children ?? [], depth + 1)]);

export const flattenPageTree = (pages: TTreeViewModel[]): TTreeViewModel[] => flattenPageTreeWithDepth(pages).map((entry) => entry.node);

export const resolvePageThumbnailUrl = (avatarValue: string | undefined, baseURL = process.env.REACT_APP_ENV_BASEURL): string | undefined => {
  const normalized = typeof avatarValue === 'string' ? avatarValue.trim() : '';
  if (!normalized || normalized === 'false') return undefined;

  const resolved = normalized.includes('https') ? normalized : `https://${baseURL}.norskinteraktiv.no/niasAPI${normalized}`;
  if (resolved.toLowerCase().includes('defaultcourseavatar')) return undefined;

  return resolved;
};

export const mapGeneratedCoursePreview = (course: TCourseViewModel, status: AICourseCreatorGenerationStatus): IAICourseCreatorGeneratedCoursePreview => {
  const flatPages = flattenPageTreeWithDepth(course.pages ?? []);
  const pages: IAICourseCreatorGeneratedPagePreview[] = flatPages.map(({ node, depth }, index) => {
    const thumbnailUrl = resolvePageThumbnailUrl(node.data?.avatar?.value);

    return {
      id: `page-${node.id}`,
      coursePageId: Number(node.id),
      sortIndex: index,
      depth,
      title: node.data?.title?.value || node.data?.name || `Page ${index + 1}`,
      status: thumbnailUrl ? 'thumbnail-ready' : 'created',
      thumbnailUrl,
    };
  });

  return {
    id: `course-${course.courseID}`,
    courseId: course.courseID,
    title: course.title,
    description: course.description,
    status,
    pages,
  };
};
